import { useCallback, useState } from 'react';
import {
  Box,
  Button,
  IndexTable,
  InlineStack,
  Text,
  Badge,
  useIndexResourceState,
} from '@shopify/polaris';
import type { BulkApprovalEntry, BulkJobError } from '../../types';

/**
 * T117 [US4]: BulkApprovalList — per-product approve/reject with IndexTable,
 * Approve All shortcut, drill-in to per-field view (FR-031).
 *
 * @see contracts/bulk.md, FR-031
 */

interface ProductRow {
  id: string;
  productId: string;
  productTitle: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
}

interface BulkApprovalListProps {
  products: ProductRow[];
  errors?: BulkJobError[];
  onApprove: (approvals: BulkApprovalEntry[]) => void;
  onDrillIn?: (productId: string) => void;
  isLoading?: boolean;
}

export function BulkApprovalList({
  products,
  errors = [],
  onApprove,
  onDrillIn,
  isLoading,
}: BulkApprovalListProps) {
  const [localStatuses, setLocalStatuses] = useState<Record<string, 'APPROVE' | 'REJECT'>>({});

  const { selectedResources, allResourcesSelected, handleSelectionChange } =
    useIndexResourceState(products as unknown as { [key: string]: unknown }[]);

  const handleApproveSelected = useCallback(() => {
    const approvals: BulkApprovalEntry[] = selectedResources.map((id) => ({
      productId: products.find((p) => p.id === id)?.productId ?? id,
      action: 'APPROVE' as const,
    }));
    onApprove(approvals);
  }, [selectedResources, products, onApprove]);

  const handleRejectSelected = useCallback(() => {
    const approvals: BulkApprovalEntry[] = selectedResources.map((id) => ({
      productId: products.find((p) => p.id === id)?.productId ?? id,
      action: 'REJECT' as const,
    }));
    onApprove(approvals);
  }, [selectedResources, products, onApprove]);

  const handleToggle = useCallback(
    (productId: string, action: 'APPROVE' | 'REJECT') => {
      setLocalStatuses((prev) => ({ ...prev, [productId]: action }));
      onApprove([{ productId, action }]);
    },
    [onApprove],
  );

  const promotedBulkActions = [
    { content: 'Approve All', onAction: handleApproveSelected },
    { content: 'Reject All', onAction: handleRejectSelected },
  ];

  const errorMap = new Map(errors.map((e) => [e.productId, e.error]));

  const rowMarkup = products.map((product, index) => {
    const error = errorMap.get(product.productId);
    const localStatus = localStatuses[product.productId];

    return (
      <IndexTable.Row
        id={product.id}
        key={product.id}
        selected={selectedResources.includes(product.id)}
        position={index}
      >
        <IndexTable.Cell>
          <Text as="span" variant="bodyMd" fontWeight="semibold">
            {product.productTitle}
          </Text>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <StatusBadge status={localStatus ?? product.status} />
        </IndexTable.Cell>
        <IndexTable.Cell>
          {error && (
            <Text as="span" variant="bodySm" tone="critical">
              {error}
            </Text>
          )}
        </IndexTable.Cell>
        <IndexTable.Cell>
          <InlineStack gap="200">
            <Button
              size="micro"
              onClick={() => handleToggle(product.productId, 'APPROVE')}
              disabled={isLoading}
            >
              Approve
            </Button>
            <Button
              size="micro"
              tone="critical"
              onClick={() => handleToggle(product.productId, 'REJECT')}
              disabled={isLoading}
            >
              Reject
            </Button>
            {onDrillIn && (
              <Button
                size="micro"
                variant="plain"
                onClick={() => onDrillIn(product.productId)}
              >
                View fields
              </Button>
            )}
          </InlineStack>
        </IndexTable.Cell>
      </IndexTable.Row>
    );
  });

  return (
    <Box>
      <IndexTable
        resourceName={{ singular: 'product', plural: 'products' }}
        itemCount={products.length}
        selectedItemsCount={
          allResourcesSelected ? 'All' : selectedResources.length
        }
        onSelectionChange={handleSelectionChange}
        promotedBulkActions={promotedBulkActions}
        headings={[
          { title: 'Product' },
          { title: 'Status' },
          { title: 'Error' },
          { title: 'Actions' },
        ]}
      >
        {rowMarkup}
      </IndexTable>
    </Box>
  );
}

function StatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'APPROVED':
    case 'APPROVE':
      return <Badge tone="success">Approved</Badge>;
    case 'REJECTED':
    case 'REJECT':
      return <Badge tone="critical">Rejected</Badge>;
    default:
      return <Badge tone="attention">Pending</Badge>;
  }
}
